import React, { useContext } from 'react';
import Navbar from 'components/navigation/web/Navbar';
import { useTranslation } from 'react-i18next';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, IconButton, Grid, Menu, MenuItem } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { getBorder } from 'utils/Styles';
import { gettingSubPage } from './ScheduleRulesSubpages';
import PatternIcon from '@mui/icons-material/Pattern';
import PaymentsIcon from '@mui/icons-material/Payments';
import TodayIcon from '@mui/icons-material/Today';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { GlobalContext } from 'GlobalContext';
//import { jwtDecode } from 'jwt-decode'
import { ContentBreadcrumbs } from 'components/navigation/web/ContentBreadcrumbs';
import { buildSubpagePath, getSubpageFromLocation } from 'utils/subpageRouting'

// Define drawer width constants
const drawerWidth = 270;
const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.paper,
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    backgroundColor: theme.palette.background.paper,
});

const SideMenu = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    height: `calc(100vh - 70px)`,
    maxHeight: `calc(100vh - 70px)`, // Asegura una altura máxima fija
    borderRight: getBorder(theme.palette.mode),
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    overflowY: 'auto', // Permite scroll vertical solo en el menú
    overflowX: 'hidden', // Previene scroll horizontal
    ...(open && {
        ...openedMixin(theme),
        backgroundColor: theme.palette.background.paper,
    }),
    ...(!open && {
        ...closedMixin(theme),
        backgroundColor: theme.palette.background.paper,
    }),
}));

const MenuItemText = styled(ListItemText)(({ theme }) => ({
    '& .MuiListItemText-primary': {
        whiteSpace: 'normal', // Permite saltos de línea
        wordWrap: 'break-word', // Rompe palabras largas
        overflow: 'hidden', // Oculta el exceso
        textOverflow: 'ellipsis', // Muestra puntos suspensivos
        display: '-webkit-box', // Para soporte de líneas múltiples con límite
        WebkitLineClamp: 2, // Limita a 2 líneas
        WebkitBoxOrient: 'vertical',
        lineHeight: 1.2, // Altura de línea reducida
        fontSize: '0.875rem', // Tamaño de fuente ligeramente más pequeño
    },
}));

/* const gettingAllowViews = () => {
    if (localStorage.getItem('access_token')) {
        return jwtDecode(localStorage.getItem('access_token'))?.views
    } else {
        return []
    }
} */

const useMenuItems = (showResume = true) => {
    const { t } = useTranslation();

    const menuItems = [
        { fullpath: '/schedule-rules?subpage=resume', routePath: '/schedule-rules/resume', path: 'resume', icon: <ManageHistoryIcon />, label: t('schedule_rules.title'), order: 0 },
        { fullpath: '/schedule-rules?subpage=shift-profile', routePath: '/schedule-rules/shift-profile', path: 'shift-profile', icon: <PaymentsIcon />, label: t('schedule_rules.submenu.shift_profile'), order: 1 },
        { fullpath: '/schedule-rules?subpage=group-shift-profile', routePath: '/schedule-rules/group-shift-profile', path: 'group-shift-profile', icon: <TodayIcon />, label: t('schedule_rules.submenu.group_shift_profile'), order: 2 },
        { fullpath: '/schedule-rules?subpage=schedule-rules', routePath: '/schedule-rules/schedule-rules', path: 'schedule-rules', icon: <PatternIcon />, label: t('schedule_rules.submenu.schedule_rules'), order: 3 },
    ];

    const visibleMenuItems = showResume ? menuItems : menuItems.filter((item) => item.path !== 'resume')

    const sortedMenuItems = visibleMenuItems.sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : Infinity;
        const orderB = b.order !== undefined ? b.order : Infinity;
        return orderA - orderB || a.label.localeCompare(b.label);
    });

    return sortedMenuItems;
};

const Component = () => {
    const theme = useTheme();
    const location = useLocation();
    const { hide, openLeft } = useContext(GlobalContext);
    const menuItems = useMenuItems(!openLeft);
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = React.useState(() => {
        const stored = localStorage.getItem('web_submenu_open');
        return stored === null ? !hide : stored === 'true';
    });

    const { subpage: paramsSubpage } = useParams();
    const selectedSubpage = getSubpageFromLocation(paramsSubpage, searchParams);

    const selectedItem = menuItems.find((item) => item.path === selectedSubpage);

    const toggleDrawer = () => {
        const nextOpen = !isOpen;
        localStorage.setItem('web_submenu_open', String(nextOpen));
        setIsOpen(nextOpen);
    };

    const quickSwitchMenus = [
        { path: '/settings/resume', label: t('settings.title'), match: '/settings' },
        { path: '/timekeeper/resume', label: t('timekeeper.title'), match: '/timekeeper' },
        { path: '/user_management/resume', label: t('user_management.title'), match: '/user_management' },
        { path: '/custom_reports/resume', label: t('custom_reports.title'), match: '/custom_reports' },
        { path: '/accruals/resume', label: t('accruals.title'), match: '/accruals' },
        { path: '/schedule-rules/resume', label: t('schedule_rules.title'), match: '/schedule-rules' },
    ];

    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

    const handleOpenQuickSwitch = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleHoverOpenQuickSwitch = (event) => {
        if (isOpen) return;
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseQuickSwitch = () => {
        setMenuAnchorEl(null);
    };

    const handleQuickSwitch = (path) => {
        handleCloseQuickSwitch();
        navigate(path);
    };

    return (
        <Box display={'flex'} sx={{ padding: 0, margin: 0 }}>
            <SideMenu open={isOpen}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        {menuItems.map((item) => (
                            <Tooltip key={item.path} title={!isOpen ? (item.path === 'resume' ? '' : item.label) : ''} placement="right">
                                <ListItemButton
                                    sx={{
                                        minHeight: 48,
                                        px: 2.5,
                                        justifyContent: isOpen ? 'initial' : 'center',
                                    }}
                                    selected={selectedSubpage === item.path}
                                    onClick={() => navigate(item.routePath || buildSubpagePath('/schedule-rules', item.path))}
                                    onMouseEnter={item.path === 'resume' ? handleHoverOpenQuickSwitch : undefined}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: isOpen ? 3 : 'auto',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <MenuItemText
                                        primary={item.label}
                                        sx={{
                                            opacity: isOpen ? 1 : 0,
                                        }}
                                    />
                                    {item.path === 'resume' && (
                                        <IconButton
                                            size='small'
                                            onClick={handleOpenQuickSwitch}
                                            sx={{ ml: 'auto', display: isOpen ? 'inline-flex' : 'none' }}
                                        >
                                            <ChevronRightIcon fontSize='small' />
                                        </IconButton>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        ))}
                    </List>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleCloseQuickSwitch}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        MenuListProps={{ onMouseLeave: handleCloseQuickSwitch }}
                    >
                        {quickSwitchMenus.map((menu) => (
                            <MenuItem key={menu.path} onClick={() => handleQuickSwitch(menu.path)}>
                                {menu.label}
                            </MenuItem>
                        ))}
                    </Menu>
                    <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: isOpen ? 'flex-end' : 'center', p: 1 }}>
                        <IconButton onClick={toggleDrawer}>
                            {isOpen ? <FirstPageIcon /> : <LastPageIcon />}
                        </IconButton>
                    </Box>
                </Box>
            </SideMenu>
            <Grid
                container
                sx={{
                    flexGrow: 1,
                    p: 2,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    ...(isOpen && {
                        transition: theme.transitions.create('margin', {
                            easing: theme.transitions.easing.easeOut,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    }),
                }}
            >
                <Grid
                    size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'hidden', // Oculta scrollbar vertical
                        overflowX: 'hidden', // Oculta scrollbar horizontal
                        height: 'calc(100vh - 100px)', // Altura fija para evitar cambios de tamaño
                    }}
                >
                    {selectedItem && selectedItem.path !== 'resume' && <ContentBreadcrumbs items={[{ label: t('home.title'), to: '/' }, { label: selectedItem.label }]} />}
                    {gettingSubPage(selectedSubpage, false, location)}
                </Grid>
            </Grid>
        </Box>
    );
};

const ScheduleRules = () => {
    const { t } = useTranslation();
    const { openLeft } = useContext(GlobalContext);
    const { subpage: paramsSubpage } = useParams();
    const [searchParams] = useSearchParams();
    const menuItems = useMenuItems(!openLeft);
    const selectedSubpage = getSubpageFromLocation(paramsSubpage, searchParams)

    return (
        <>
            <Navbar
                view={{
                    title: t('schedule_rules.title') + ' - ' + menuItems.find((item) => item.path === selectedSubpage)?.label,
                    component: <Component />,
                }}
                right={{
                    title: 'Right',
                    component: <> componente </>,
                }}
            />
        </>
    );
};

export { ScheduleRules,  };
